import { Button } from '@heidi/design-system/button';
import { Skeleton } from '@heidi/design-system/skeleton';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check, File, FileText } from 'lucide-react';
import { useState } from 'react';
import { useIntl, type IntlShape, type MessageDescriptor } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import type { ObjectWorkspaceAnchor } from './objectWorkspace.data';

interface AnchorListProps {
  anchors: readonly ObjectWorkspaceAnchor[];
  onSelect?: (anchor: ObjectWorkspaceAnchor) => void;
}

function formatAnchorDate(intl: IntlShape, occurredAt: string): string {
  const date = new Date(occurredAt);
  return `${intl.formatDate(date, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })} · ${intl.formatTime(date, { hour: 'numeric', minute: '2-digit' })}`;
}

export function AnchorList({ anchors, onSelect }: AnchorListProps): JSX.Element {
  const intl = useIntl();
  const [selectedId, setSelectedId] = useState<string>();

  return (
    <div className="flex flex-col gap-1">
      {anchors.map((anchor) => {
        const selected = selectedId === anchor.id;
        const Icon = anchor.kind === 'session' ? FileText : File;
        const dateLabel =
          anchor.occurredAtLabel ??
          (anchor.occurredAt ? formatAnchorDate(intl, anchor.occurredAt) : undefined);

        return (
          <Button
            key={anchor.id}
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={selected}
            className={cn(
              'h-auto w-full justify-start gap-3 whitespace-normal rounded-lg px-3 py-2.5 text-start',
              selected && 'bg-surface-selected hover:bg-surface-selected-hover'
            )}
            onClick={() => {
              setSelectedId(anchor.id);
              onSelect?.(anchor);
            }}
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-background-tertiary">
              <Icon className="size-4 text-text-secondary" aria-hidden />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span
                className={cn(
                  typographyVariant({ variant: 'p2' }),
                  'line-clamp-2 text-text-primary'
                )}
              >
                {anchor.title}
              </span>
              {dateLabel ? (
                <span
                  className={cn(
                    typographyVariant({ variant: 'caption' }),
                    'text-text-tertiary'
                  )}
                >
                  {dateLabel}
                </span>
              ) : null}
              {anchor.detail ? (
                <span
                  className={cn(
                    typographyVariant({ variant: 'caption' }),
                    'line-clamp-1 text-text-secondary'
                  )}
                >
                  {anchor.detail}
                </span>
              ) : null}
            </span>
            {selected ? (
              <Check className="size-4 shrink-0 text-text-primary" aria-hidden />
            ) : null}
          </Button>
        );
      })}
    </div>
  );
}

export function AnchorListLoading({
  label = messages.loadingPatientSessions,
}: {
  label?: MessageDescriptor;
}): JSX.Element {
  const intl = useIntl();

  return (
    <div
      aria-busy="true"
      aria-label={intl.formatMessage(label)}
      className="flex flex-col gap-1"
      role="status"
    >
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="size-8 shrink-0 rounded-lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface PaginatedAnchorListProps extends AnchorListProps {
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onShowMore?: () => void;
}

export function PaginatedAnchorList({
  anchors,
  onSelect,
  hasMore = false,
  isLoadingMore = false,
  onShowMore,
}: PaginatedAnchorListProps): JSX.Element {
  const intl = useIntl();

  return (
    <div className="flex flex-col gap-1">
      <AnchorList anchors={anchors} onSelect={onSelect} />
      {hasMore && onShowMore ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={isLoadingMore}
          aria-busy={isLoadingMore}
          onClick={onShowMore}
        >
          {intl.formatMessage(messages.showMorePatientSessions)}
        </Button>
      ) : null}
    </div>
  );
}
